import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCard } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, filter, Subscription, take } from 'rxjs';
import { CurrenciesService } from '../../currencies/services/currencies.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { DetailPair } from '../../../shared/components/detail-info-card/DetailPair';
import { formatCurrencyString } from '../../../utilities/CurrencyUtility';
import { DeletePolicyUnderwriterDialogComponent } from '../../policies/policy-detail/tabs/policy-underwriters/components/delete-policy-underwriter-dialog/delete-policy-underwriter-dialog.component';
import { NightlyPricesColumns, NightlyPrice, getPriceRowFromPrice } from '../../policies/models/NightlyPrices';
import { PolicyPrice } from '../../policies/models/Policy';
import { PriceRatesService } from '../services/price-rates.service';
import { DetailInfoCardComponent } from '../../../shared/components/detail-info-card/detail-info-card.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';

@Component({
  selector: 'app-price-rate-detail',
  templateUrl: './price-rate-detail.component.html',
  styleUrls: ['./price-rate-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    DetailInfoCardComponent,
    MatCard,
    MatTableModule,
    MatProgressSpinner,
    MatButtonModule,
    BackButtonComponent
  ]
})
export class PriceRateDetailComponent implements OnInit {

  private routeSub?: Subscription;

  loading = new BehaviorSubject<boolean>(false);
  dataSource = new MatTableDataSource<any>();
  priceRate!: any;
  priceRateId!: string;
  priceRateDetails: DetailPair[] = [];
  tieredNightsDetails: DetailPair[] = [];

  extraNightPrice: any;
  displayedColumnNames: string[] = [];
  displayedColumns = NightlyPricesColumns;

  constructor(
    private priceRatesService: PriceRatesService,
    private currenciesService: CurrenciesService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    public userContextProvider: UserContextProvider,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.routeSub = this.route.params.subscribe(params => {
        this.priceRateId = params['id'];
        this.loadPage();
      });
    });
  }

  async loadPage() {
    try {
      this.loading.next(true);

      const response = await this.priceRatesService.getPriceRateDetail(this.priceRateId);
      this.priceRate = response;
      this.populatePriceRateDetails();

      if (this.priceRate.night_ranges.length > 0) {
        this.populatePriceRateNightRanges(this.priceRate);
        this.initTableColumns();
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Price Rate details', 'Error while retrieving Price Rate details');
    } finally {
      this.loading.next(false);
    }
  }

  async populatePriceRateDetails() {
    try {
      this.priceRateDetails = [];
      const priceModel = this.priceRatesService.getPriceModelById(this.priceRate.pricing_model_id);

      let priceFor;
      if (this.priceRate.type_id === 1) {
        priceFor = 'Underwriter';
      } else {
        priceFor = 'Property Manager';
      }

      this.priceRateDetails.push(new DetailPair("ID", this.priceRate.id));
      this.priceRateDetails.push(new DetailPair("Price For", priceFor));
      this.priceRateDetails.push(new DetailPair("Pricing Model", priceModel));
      this.priceRateDetails.push(new DetailPair("Deductible", formatCurrencyString(this.priceRate.deductible, this.userContextProvider.currencyCode)));
      this.priceRateDetails.push(new DetailPair("Currency", this.priceRate.currency.code));
      this.priceRateDetails.push(new DetailPair("Description", this.priceRate.description));
      this.priceRateDetails.push(new DetailPair("Nightly Price", formatCurrencyString(this.priceRate.price, this.userContextProvider.currencyCode)));
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Price Rate details', 'Error while retrieving Price Rate details');
    }
  }

  async populatePriceRateNightRanges(priceRate: any) {
    this.dataSource = new MatTableDataSource(this.getPriceRangeRows(priceRate));
    this.extraNightPrice = this.currenciesService.prependCurrencySymbol(priceRate.currency, `${priceRate.extra_night_price}`);
  }

  getPriceRangeRows(policyPrice: PolicyPrice) {
    return policyPrice.night_ranges.map((price: NightlyPrice) => {
      return getPriceRowFromPrice(price, policyPrice.currency, this.currenciesService);
    });
  }

  onEditPriceRateClick() {
    this.router.navigate(['/price-rates/form'], { queryParams: { id: this.priceRateId } });
  }

  async onDeletePriceRateClick(): Promise<void> {
    const deleteDialogRef = this.dialog.open(DeletePolicyUnderwriterDialogComponent, {
      width: '50vw',
      maxHeight: '265px',
      disableClose: false,
      data: 'Price Rate'
    });
    deleteDialogRef.afterClosed().subscribe(async confirmed => {
      if (confirmed > 0) {
        let payload = {
          is_active: 0,
          status_id: 2,
          original_id: this.priceRate.id
        }
        
        this.priceRatesService.updatePriceRate(this.priceRate.id, payload).then(_ => {
          this.navigateToPriceRatesPage();
        });
      }
    });
  }

  initTableColumns(): void {
    this.displayedColumnNames = NightlyPricesColumns.map(c => c.column);
  }

  navigateToPriceRatesPage() {
    this.router.navigate(['/price-rates']);
  }
}